import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Reveal, { EASE } from './Reveal';
import SectionHeading from './SectionHeading';
import ProductModal from './ProductModal';
import { PRODUCTS } from '../../data/site';

const ProductCard = ({ product, index, onOpen }) => (
  <motion.article
    data-testid={`product-card-${product.id}`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.9, delay: index * 0.1, ease: EASE }}
    whileHover={{ y: -6 }}
    className="group flex flex-col overflow-hidden rounded-3xl border border-line bg-white shadow-card transition-shadow duration-500 hover:shadow-lift"
  >
    <div
      className="spotlight-img relative h-56 overflow-hidden"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
        e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
      }}
    >
      <img
        src={product.image}
        alt={product.alt}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110"
      />
      <span className="absolute left-4 top-4 z-[2] rounded-full bg-cream/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-forest backdrop-blur">
        Export Grade
      </span>
    </div>
    <div className="flex flex-1 flex-col p-6">
      <h3 className="font-display text-xl font-semibold text-ink">{product.name}</h3>
      <p className="mt-2.5 flex-1 text-sm leading-relaxed text-muted-foreground">{product.short}</p>
      <button
        data-testid={`product-read-more-${product.id}`}
        onClick={() => onOpen(product)}
        className="mt-5 inline-flex w-fit items-center gap-2 text-sm font-bold text-forest transition-colors hover:text-gold-dark"
      >
        Read More
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
      </button>
    </div>
  </motion.article>
);

export const Products = () => {
  const [selected, setSelected] = useState(null);

  return (
    <section id="products" data-testid="products-section" className="relative bg-cream-deep py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="Our Products"
            title={
              <>
                Dehydrated onion products, <span className="accent-italic font-semibold">export prepared.</span>
              </>
            }
          />
          <Reveal delay={0.2}>
            <p className="max-w-sm text-sm leading-relaxed text-muted-foreground lg:text-right">
              Currently supplying four core onion products. Specifications are finalised with each buyer —{' '}
              <span className="font-semibold text-forest">as per PO and batch COA.</span>
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PRODUCTS.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} onOpen={setSelected} />
          ))}
        </div>
      </div>

      <ProductModal product={selected} onClose={() => setSelected(null)} />
    </section>
  );
};

export default Products;
